import React, { useReducer, useEffect } from "react";
import DialogContent from "@mui/material/DialogContent";
import dayjs from "dayjs";
import { get } from "aws-amplify/api";
import currentUserToken from "CurrentUserToken";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  TablePagination,
} from "@mui/material";
import { Typography } from "@mui/material";
import { I18n } from "aws-amplify/utils";
import Stack from "@mui/material/Stack";
import Skeleton from "@mui/material/Skeleton";
import Grid from "@mui/material/Grid";

const reducer = (lastState, action) => {
  switch (action.type) {
    case "DATA_RECEIVED":
      const accountNumbers = action.value;
      if (Array.isArray(accountNumbers)) {
        return { ...lastState, accountNumbers: accountNumbers, fetched: true };
      }
      return lastState;
    case "SET_SEARCH_TERM":
      return { ...lastState, searchTerm: action.value, page: 0 };
    case "SET_PAGE":
      return { ...lastState, page: action.value };
    case "SET_ROWS_PER_PAGE":
      return { ...lastState, rowsPerPage: action.value, page: 0 };
    default:
      return lastState;
  }
};

function AccountNumberStep(props) {
  const [state, dispatch] = useReducer(reducer, {
    accountNumbers: [],
    fetched: false,
    searchTerm: "",
    page: 0,
    rowsPerPage: 25,
  });

  useEffect(() => {
    const fetchAccountNumbers = async () => {
      try {
        const restOperation = get({
          apiName: "TelemonitoringAPI",
          path: "/accountNumber/get",
          options: {
            headers: {
              "Content-Type": "application/json",
              Authorization: `${await currentUserToken()}`,
            },
          },
        });
        const { body } = await restOperation.response;
        const response = await body.json();

        dispatch({ type: "DATA_RECEIVED", value: response });
      } catch (error) {
        console.error(I18n.get("Error during API call:"), error);
      }
    };

    fetchAccountNumbers();
  }, []);

  const columns = [
    {
      field: "account_number",
      headerName: I18n.get("Account Number"),
      align: "left",
      flex: 1,
    },
    {
      field: "name",
      headerName: I18n.get("Account Name"),
      align: "left",
      flex: 1,
      renderCell: (params) => (
        <Typography
          component="a"
          href={`https://eptarefrigeration.lightning.force.com/${params.row.id}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            fontSize: "medium",
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "store_name",
      headerName: I18n.get("Store Name"),
      align: "left",
      flex: 1,
      renderCell: (params) => (
        <Typography
          component="a"
          href={`https://eptarefrigeration.lightning.force.com/${params.row.id}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            fontSize: "medium",
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "billing_country",
      headerName: I18n.get("Country"),
      align: "left",
      flex: 1,
    },
    {
      field: "billing_city",
      headerName: I18n.get("City"),
      align: "left",
      flex: 1,
    },
    {
      field: "retailer_description",
      headerName: I18n.get("Retailer"),
      align: "left",
      flex: 1,
    },
    { field: "id", headerName: I18n.get("Account ID"), align: "left", flex: 1 },
 
  ];

  const handleRowClick = (row) => {
    props.setSelectedAccountNumber(row);
    props.handleMainFormData("account_id", row.id);
    props.handleMainFormData("account_number", row.account_number);
  };

  const filteredAccountNumbers = state.accountNumbers.filter((row) => {
    const searchTerm = state.searchTerm.toLowerCase();
    return Object.values(row).some(
      (value) => value && value.toString().toLowerCase().includes(searchTerm)
    );
  });

  const { page, rowsPerPage } = state;

  const displayAccountNumbers = filteredAccountNumbers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <DialogContent>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label={I18n.get("Search")}
            variant="outlined"
            fullWidth
            value={state.searchTerm}
            onChange={(e) =>
              dispatch({ type: "SET_SEARCH_TERM", value: e.target.value })
            }
            sx={{ marginBottom: 2 }}
          />
          <TableContainer>
            {!state.fetched ? (
              <Stack alignItems="center" sx={{ height: "100%" }}>
                <Skeleton
                  variant="rounded"
                  height={30}
                  width="100%"
                  sx={{ margin: "2px", marginTop: "5px" }}
                />
                <Skeleton
                  variant="rounded"
                  height={30}
                  width="100%"
                  sx={{ margin: "2px" }}
                />
                <Skeleton
                  variant="rounded"
                  height={30}
                  width="100%"
                  sx={{ margin: "2px" }}
                />
              </Stack>
            ) : (
              <>
                <Table>
                  <TableHead>
                    <TableRow>
                      {columns.map((column) => (
                        <TableCell key={column.field}>
                          {column.headerName}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayAccountNumbers.map((row) => (
                      <TableRow
                        key={row.account_number}
                        onClick={() => handleRowClick(row)}
                        selected={row === props.selectedAccountNumber}
                      >
                        {columns.map((column) => (
                          <TableCell key={column.field}>
                            {row[column.field]}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={filteredAccountNumbers.length}
                  page={state.page}
                  onPageChange={(event, newPage) =>
                    dispatch({ type: "SET_PAGE", value: newPage })
                  }
                  rowsPerPage={state.rowsPerPage}
                  onRowsPerPageChange={(event) =>
                    dispatch({
                      type: "SET_ROWS_PER_PAGE",
                      value: parseInt(event.target.value, 10),
                    })
                  }
                  rowsPerPageOptions={[25]}
                />
              </>
            )}
          </TableContainer>
        </Grid>
      </Grid>
    </DialogContent>
  );
}

export default AccountNumberStep;
